import React, { useEffect, useState, useMemo } from 'react'
import { useSetRecoilState } from 'recoil'
import { navigate } from 'gatsby'
import { Col, Row, Stack } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { CustomerAddressElement } from '/src/components/commerce/form/elements'
import { Button, Textarea, TextInput } from '/src/components/common'
import { PickupGuidelinesElement } from '/src/components/commerce'
import { Loading } from '/src/components/loading'
import { cartState } from '/src/hooks/useCart'
import { postRequest } from '/src/httpClient'

const FormatStorePickupAddress = ({ name, field_mc_store_address, field_mc_store_pickup_estimate }) => {
  const { address_line1, locality, administrative_area, postal_code } = field_mc_store_address

  return (
    <div className="store-address-container">
      <Stack direction="vertical">
        <h5 className="mb-2">{name}</h5>
        <label className="mb-1">{address_line1}, {locality} {administrative_area} {postal_code}</label>
        <span>{field_mc_store_pickup_estimate}</span>
      </Stack>
    </div>
  )
}

const hasErrorClass = (name, errors) => {
  return errors.hasOwnProperty(name) ? 'error' : ''
}

const ShippingPickupPane = ({ cart, errors, register }) => {
  return (
    <>
      <div className="form-item-address address mb-4">
        <label className="mb-2">Pickup From</label>
        {cart.store && (
          <FormatStorePickupAddress {...cart.store} />
        )}
      </div>
      <div className={"form-item-radios pickup-using mb-4 " + hasErrorClass('pickup_using', errors)}>
        <label className="mb-2">Pickup Using</label>
        <Row>
          <Col sm={6}>
            <div className="form-item radio">
              <label>
                Self Pickup
                <input type="radio" value="self" {...register('pickup_using', {
                  required: "Please select a pickup method"
                })} />
              </label>
            </div>
          </Col>
          <Col sm={6}>
            <div className="form-item radio">
              <label>
                Transport Company
                <input type="radio" value="transport" {...register('pickup_using', {
                  required: "Please select a pickup method"
                })} />
              </label>
            </div>
          </Col>
        </Row>
      </div>
      <div className="form-item-guidelines pickup-guidelines mb-4">
        <label className="mb-2">Pickup Guidelines</label>
        <p>To ensure a seamless pickup experience, please review and agree to the following pickup guidelines. Need help? Call 137 833 or Send Enquiry</p>
        <PickupGuidelinesElement 
          label="I acknowledge and agree to the Pickup Guidelines*"
          store={cart.store}
          error={errors.guidelines}
          {...register('guidelines', {
            required: true
          })} />
      </div>
      <div className="form-item-textarea pickup-notes mb-4">
        <label className="mb-2">Phone Number</label>
        <p>Please provide a contact number for your order.</p>
        <TextInput error={errors.phone_number} register={register('phone_number', {
          required: "Please provide a phone number"
        })} />
      </div>
      <div className="form-item-textarea pickup-notes mb-4">
        <label className="mb-2">Pickup Notes</label>
        <p>When using self pickup to collect your order, please include any relevant details to assist us (up to 200 characters). Need help? Call 137 833 or Send Enquiry.</p>
        <Textarea placeholder="Vehicle type, weight limits for roof racks etc" register={register('pickup_notes')} />
      </div>
    </>
  )
}

const ShippingDeliveryPane = ({ cart, user, errors, control, register, setValue, getValues, watch }) => {
  return (
    <>
      <div className="form-item-radios deliver-to mb-4">
        <label className="mb-2">Deliver to</label>
        <Row>
          <Col sm={6}>
            <div className="form-item radio">
              <label>
                My Address
                <input type="radio" value="address" {...register('deliver_to')} />
              </label>
            </div>
          </Col>
          <Col sm={6}>
            <div className="form-item radio">
              <label>
                Local Depot
                <input disabled type="radio" value="depot" {...register('deliver_to')} />
              </label>
            </div>
          </Col>
        </Row>
      </div>
      <CustomerAddressElement 
        label="Delivery Address"
        cart={cart}
        user={user}
        control={control} 
        register={register}
        setValue={setValue}
        getValues={getValues}
        watch={watch}
      />
      <div className="form-item-textarea pickup-notes mb-4">
        <label className="mb-2">Phone Number</label>
        <p>Please provide a contact number for your order.</p>
        <TextInput error={errors.phone_number} register={register('phone_number', {
          required: "Please provide a phone number"
        })} />
      </div>
      <div className="form-item-textarea delivery-notes mb-4">
        <label className="mb-2">Delivery Notes</label>
        <p>Please include any relevant details to assist with the delivery (up to 200 characters)
        Need help? Call 137 833 or Send Enquiry.</p>
        <Textarea placeholder="Site contact, preferred entry point, height restrictions, acessibility etc." register={register('delivery_notes')} />
      </div>
    </>
  )
}

const validateDeliveryOrder = (cart, user) => {
  const { order_total, shipping_information } = cart.attributes

  if (!shipping_information || !shipping_information.address.hasOwnProperty('address_line1')) {
    return false
  }

  const hasShipmentPrice = order_total.adjustments.find(value => value.type === 'shipping')
  if (!hasShipmentPrice || hasShipmentPrice.total.number === '0.00' || hasShipmentPrice.total.number === '0.0' || hasShipmentPrice.total.number === '0') {
    if (user.trade) {
      return true
    }
    return false
  }

  return true
}

const validatePickupOrder = (data) => {
  if (!data.guidelines) {
    return false
  }

  return true
}

const getPhoneNumber = (cart, user) => {
  if (cart.attributes.field_phone_number) {
    return cart.attributes.field_phone_number
  }

  if (user && user.field_phone) {
    return user.field_phone
  }

  return null
}

export const ShippingPaneForm = ({ cart, user }) => {
  const { attributes, shipments } = cart
  const setCart = useSetRecoilState(cartState)
  const { register, watch, control, setValue, getValues, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      // shipping_method: attributes.shipping_method,
      equipment: attributes.field_unloading_equipment ? '1' : '0',
      deliver_to: 'address',
      pickup_using: attributes.field_pickup_using,
      pickup_notes: attributes.field_pickup_notes,
      phone_number: getPhoneNumber(cart, user),
    }
  })

  const [shippingMethod, setShippingMethod] = useState(getValues('shipping_method'))
  const [isLoading, setIsLoading] = useState(false)
  const canPickup = useMemo(() => cart.store && cart.store.dsap_pickup_plant === '1' ? false : true, [cart.store])

  watch('shipping_method')
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'shipping_method') {
        setIsLoading(true)

        // Remove the shipment relationship for pickup order.
        if (value.shipping_method === '1--default') {
          postRequest('/api/cart/updatePickup', {
            data: {
              id: cart.id,
              type: 'order--default',
              attributes: {
                shipping_method: value.shipping_method,
                shipping_information: {
                  address: {
                    country: 'AU'
                  }
                },
                order_total: {
                  adjustments: []
                }
              },
              relationships: {
                shipments: {
                  data: []
                }
              }
            },
            shipments: shipments
          }).then(values => {
            if (values) {
              setCart(prev => {
                return {
                  ...prev,
                  attributes: values.attributes
                }
              })
            }

            setShippingMethod(value.shipping_method)
            setIsLoading(false)
          })
        }
        else {
          postRequest('/api/cart/update', {
            id: cart.id,
            type: 'order--default',
            attributes: {
              shipping_method: value.shipping_method,
            }
          }).then(values => {
            if (values) {
              setCart(prev => {
                return {
                  ...prev,
                  attributes: values.attributes,
                  shipments: values.relationships.shipments.data
                }
              })
            }

            setShippingMethod(value.shipping_method)
            setIsLoading(false)
          })
        }
      }
    })
    return () => subscription.unsubscribe()
  }, [watch])

  const onSubmit = (data) => {
    const { shipping_method } = data

    if (!shipping_method) {
      // form error
      return
    }

    window.dataLayer = window.dataLayer || []
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'checkoutPaneShippingSubmit',
        order: {
          id: cart.id,
          number: cart.attributes.order_number,
          total: cart.attributes.order_total.total.number,
          user: cart.attributes.email,
          order_items: cart.relationships.order_items.data.map((item) => {
            return {
              id: item.id,
              ref: item.attributes.product_reference,
              title: item.attributes.title,
              quantity: item.attributes.quantity,
              total_price: item.attributes.total_price.number,
              unit_price: item.attributes.unit_price.number,
              attributes: item.attributes.variation_attributes?.map((attribute) => {
                return {
                  label: attribute.label,
                  value: attribute.value
                }
              })
            }
          }),
          shipping_method: shipping_method,
        },
        store: {
          name: cart.store.name,
          plant_id: cart.store.dsap_plant_id
        }
      })
    }

    // Clientside validation
    // @todo, amend validation see react hook form.
    if (shipping_method === '4--delivery') {
      if (validateDeliveryOrder(cart, user)) {
        postRequest('/api/cart/update', {
          id: cart.id,
          type: 'order--default',
          attributes: {
            field_deliver_to: data.deliver_to,
            field_delivery_notes: data.delivery_notes,
            field_phone_number: data.phone_number,
            shipping_method: '4--delivery'
          }
        }).then(values => {
          if (values) navigate('/checkout/payment')
        })
      }
    }
    else if (validatePickupOrder(data)) {
      postRequest('/api/cart/updatePickup', {
        data: {
          id: cart.id,
          type: 'order--default',
          attributes: {
            field_pickup_using: data.pickup_using,
            field_pickup_notes: data.pickup_notes,
            field_phone_number: data.phone_number,
            shipping_method: '1--default'
          },
          relationships: {
            shipments: {
              data: []
            }
          }
        },
        shipments: shipments
      }).then(values => {
        if (values) navigate('/checkout/payment')
      })
    }
    else {
      // failed validation.
    }
  }

  let ShippingPaneBase = <></>
  if (shippingMethod === '1--default') {
    ShippingPaneBase = <ShippingPickupPane
      cart={cart}
      user={user}
      errors={errors}
      register={register} />
  }
  else if (shippingMethod === '4--delivery') {
    ShippingPaneBase = <ShippingDeliveryPane 
      cart={cart}
      user={user}
      errors={errors}
      control={control}
      register={register}
      setValue={setValue}
      getValues={getValues}
      watch={watch} />
  }

  return (
    <div className="shipping-form-container">
      <form id="shipping-pane-form" onSubmit={handleSubmit(onSubmit)}>
        <div className="form-item-radios shipping-method mb-4">
          <label className="mb-2">Shipping Method</label>
          <Row>
            <Col sm={6} lg={4}>
              <div className="form-item radio">
                <label>
                  Pickup
                  <input type="radio" value="1--default" disabled={canPickup} {...register('shipping_method')} />
                </label>
              </div>
            </Col>
            <Col sm={6} lg={4}>
              <div className="form-item radio">
                <label>
                  Delivery
                  <input type="radio" value="4--delivery" {...register('shipping_method')} />
                </label>
              </div>
            </Col>
          </Row>
        </div>
        {isLoading ? (
          <Loading />
        ) : (
          <>
            {ShippingPaneBase}
            <Button variant="primary" type="submit">
              To Payment
            </Button>
          </>
        )}
      </form>
    </div>
  )
}

export default ShippingPaneForm
